/**
 * Envio de documento para a pasta aberta. A organização sai da pasta, no backend;
 * por isso não há seletor de organização aqui. Com `triagem` (arquivos soltos do
 * computador, spec 2026-10-19-kb-arrastar-e-soltar D6) o mesmo modal envia vários, um por
 * vez e sem título: o servidor usa o nome do arquivo.
 *
 * Antes de subir, cada arquivo é conferido pelo sha256 (spec 2026-10-22-kb-arquivo-repetido-
 * e-atalho, §3): o que já está na organização não sobe de novo, e o aviso diz onde ele está.
 * Se a conferência falhar por rede, o envio segue e o 409 do servidor traz o mesmo aviso.
 */
import { Fragment, useRef, useState } from 'react'
import { Alert, Button, FileInput, Group, Modal, Progress, Select, Stack, Table, TagsInput, Text, TextInput, Textarea } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { IconAlertTriangle, IconFolderShare, IconFolderSymlink, IconUpload } from '@tabler/icons-react'
import { criarKbAtalho, updateKbDocument, uploadKbDocument, type KbEnvironment, type KbVerificacao } from '@/api/kb'
import { mensagemDeErro } from './acesso'
import { AvisoDeRepetido, CONSULTAS_DA_PASTA, podeMoverOriginal } from './AvisoDeRepetido'
import { MAX_ARQUIVOS_POR_SOLTURA, MIME_ACCEPT, MOTIVO_DIRETORIO, recusaDe, repetidoDoErro, verificarArquivo, type Triagem } from './envio'
import { useLimitesDaKb } from './limites'

export const ENVIRONMENT_OPTIONS: { value: KbEnvironment; label: string }[] = [
  { value: 'prd', label: 'PRD' },
  { value: 'hml', label: 'HML' },
  { value: 'qa', label: 'QA' },
  { value: 'dev', label: 'DEV' },
  { value: 'stg', label: 'STG' },
  { value: 'sandbox', label: 'Sandbox' },
]

interface Props {
  /** Pasta de destino; `null` fecha o modal. Com `triagem`, envia os arquivos soltos. */
  pasta: { id: number; caminho: string; triagem?: Triagem } | null
  onClose: () => void
  /** Abre a pasta do original, pelo link do aviso de arquivo repetido */
  onAbrirPasta?: (id: number) => void
}

const PRIVACIDADE = 'O conteúdo deste documento será enviado à OpenAI para geração de embeddings. Imagens e PDFs escaneados também passam pelo modelo de visão.'

function AvisoDePrivacidade() {
  return (
    <Alert color="yellow" icon={<IconAlertTriangle size={14} />} title="Aviso de privacidade">
      {PRIVACIDADE}
    </Alert>
  )
}

export function EnvioDeDocumento({ pasta, onClose, onAbrirPasta }: Props) {
  // O link para a pasta do original fecha o envio antes de abri-la.
  const abrirPasta = onAbrirPasta ? (id: number) => { onClose(); onAbrirPasta(id) } : undefined
  if (pasta?.triagem) {
    return (
      <Modal opened onClose={onClose} title={<Text fw={700}>Enviar documentos</Text>} size="lg">
        <EnvioDeVarios pasta={{ id: pasta.id, caminho: pasta.caminho }} triagem={pasta.triagem} onClose={onClose} onAbrirPasta={abrirPasta} />
      </Modal>
    )
  }
  return <EnvioDeUm pasta={pasta} onClose={onClose} onAbrirPasta={abrirPasta} />
}

type Fase =
  | { fase: 'fila' | 'conferindo' | 'enviado' | 'atalho' | 'movido' }
  | { fase: 'enviando'; fracao: number }
  | { fase: 'erro'; mensagem: string }
  | { fase: 'repetido'; v: KbVerificacao }

/** A conferência achou o arquivo na base: não sobe de novo. */
function ehRepetido(v: KbVerificacao | null): v is KbVerificacao {
  return v !== null && v.situacao !== 'novo'
}

function tamanho(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`
}

function porcento(fracao: number): string {
  return `${Math.round(fracao * 100)}%`
}

function rotuloDaFase(f: Fase): { texto: string; cor?: string } {
  if (f.fase === 'erro') return { texto: f.mensagem, cor: 'red' }
  // 100% enviado ainda espera a api guardar o arquivo: dizer "Enviado" antes seria cedo.
  if (f.fase === 'enviando') return { texto: f.fracao >= 1 ? 'Guardando…' : `Enviando ${porcento(f.fracao)}`, cor: 'dimmed' }
  if (f.fase === 'repetido') return { texto: 'Não enviado', cor: 'yellow' }
  const textos = { fila: 'Na fila', conferindo: 'Conferindo…', enviado: 'Enviado', atalho: 'Atalho criado', movido: 'Movido para cá' }
  return { texto: textos[f.fase], cor: f.fase === 'fila' || f.fase === 'conferindo' ? 'dimmed' : 'teal' }
}

type Lote = 'atalho' | 'mover'

function EnvioDeVarios({ pasta, triagem, onClose, onAbrirPasta }: { pasta: { id: number; caminho: string }; triagem: Triagem; onClose: () => void; onAbrirPasta?: (id: number) => void }) {
  const qc = useQueryClient()
  const [env, setEnv] = useState<string | null>(null)
  const [tags, setTags] = useState<string[]>([])
  const [fases, setFases] = useState<Record<number, Fase>>({})
  const [enviando, setEnviando] = useState(false)
  const [terminou, setTerminou] = useState(false)
  const [lote, setLote] = useState<Lote | null>(null)
  const [noLote, setNoLote] = useState(false)
  const aceitos = triagem.itens.filter((i) => i.recusa === null).length
  const podeEnviar = !triagem.excedeu && aceitos > 0 && !terminou
  const definir = (i: number, f: Fase) => setFases((atual) => ({ ...atual, [i]: f }))

  // Repetidos em outra pasta: com 2 ou mais, uma ação para todos (spec §3; a Ane tinha 28).
  const emOutraPasta = Object.entries(fases)
    .flatMap(([i, f]) => (f.fase === 'repetido' && f.v.situacao === 'outra_pasta' && f.v.documento ? [{ i: Number(i), v: f.v }] : []))
  const moviveis = emOutraPasta.filter((r) => podeMoverOriginal(r.v))
  const doLote = lote === 'mover' ? moviveis : emOutraPasta

  async function executarLote(qual: Lote) {
    setNoLote(true)
    const alvos = qual === 'mover' ? moviveis : emOutraPasta
    let ok = 0
    // Um por vez, e cada arquivo grava o próprio evento na trilha.
    for (const { i, v } of alvos) {
      try {
        if (qual === 'atalho') await criarKbAtalho(v.documento!.id, pasta.id)
        else await updateKbDocument(v.documento!.id, { folder_id: pasta.id })
        ok += 1
        definir(i, { fase: qual === 'atalho' ? 'atalho' : 'movido' })
      } catch (e) {
        definir(i, { fase: 'erro', mensagem: mensagemDeErro(e, 'Falha na operação.') })
      }
    }
    setNoLote(false)
    setLote(null)
    for (const k of CONSULTAS_DA_PASTA) qc.invalidateQueries({ queryKey: [k] })
    const n = alvos.length
    notifications.show({
      color: ok === n ? 'teal' : 'yellow',
      title: qual === 'atalho' ? 'Atalhos criados' : 'Documentos movidos',
      message: qual === 'atalho' ? `${ok} de ${n} atalhos criados` : `${ok} de ${n} documentos movidos para cá`,
    })
  }

  async function enviarTodos() {
    setEnviando(true)
    let ok = 0
    let repetidos = 0
    // Um por vez (D6): a rota recebe um arquivo por chamada, e o erro de um não para os outros.
    // O sha256 também vai um por vez: cada arquivo, até 100 MB, fica inteiro em memória (R2).
    for (const [i, item] of triagem.itens.entries()) {
      if (item.recusa) continue
      definir(i, { fase: 'conferindo' })
      const v = await verificarArquivo(pasta.id, item.arquivo)
      if (ehRepetido(v)) {
        repetidos += 1
        definir(i, { fase: 'repetido', v })
        continue
      }
      setFases((f) => ({ ...f, [i]: { fase: 'enviando', fracao: 0 } }))
      const fd = new FormData()
      fd.append('file', item.arquivo)
      if (env) fd.append('environment', env)
      if (tags.length) fd.append('tags', tags.join(','))
      try {
        await uploadKbDocument(pasta.id, fd, (fracao) => setFases((f) => ({ ...f, [i]: { fase: 'enviando', fracao } })))
        ok += 1
        setFases((f) => ({ ...f, [i]: { fase: 'enviado' } }))
      } catch (e) {
        const r = repetidoDoErro(e)
        if (r) {
          repetidos += 1
          definir(i, { fase: 'repetido', v: r })
        } else {
          setFases((f) => ({ ...f, [i]: { fase: 'erro', mensagem: mensagemDeErro(e, 'Falha ao enviar o arquivo.') } }))
        }
      }
    }
    setEnviando(false)
    setTerminou(true)
    for (const k of CONSULTAS_DA_PASTA) qc.invalidateQueries({ queryKey: [k] })
    const total = triagem.itens.length
    const jaNaBase = repetidos === 0 ? '' : ` · ${repetidos} já ${repetidos === 1 ? 'estava' : 'estavam'} na base`
    notifications.show({ color: ok === total ? 'teal' : 'yellow', title: 'Envio concluído', message: `${ok} de ${total} arquivos enviados${jaNaBase}` })
    if (ok === total) onClose()
  }

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">Para: {pasta.caminho}</Text>
      {triagem.excedeu && (
        <Alert color="red" icon={<IconAlertTriangle size={14} />}>
          Solte no máximo {MAX_ARQUIVOS_POR_SOLTURA} arquivos de uma vez: vieram {triagem.itens.length}.
        </Alert>
      )}
      {triagem.diretorios > 0 && <Alert color="gray">{MOTIVO_DIRETORIO}</Alert>}
      {!triagem.excedeu && triagem.itens.length > 0 && (
        <Table verticalSpacing={4} aria-label="Arquivos">
          <Table.Tbody>
            {triagem.itens.map((item, i) => {
              const fase = fases[i] ?? { fase: 'fila' }
              const r = item.recusa ? { texto: item.recusa, cor: 'red' } : rotuloDaFase(fase)
              return (
                <Fragment key={i}>
                  <Table.Tr>
                    <Table.Td><Text size="sm">{item.arquivo.name}</Text></Table.Td>
                    <Table.Td><Text size="xs" ff="monospace">{tamanho(item.arquivo.size)}</Text></Table.Td>
                    <Table.Td><Text size="xs" c={r.cor}>{r.texto}</Text></Table.Td>
                  </Table.Tr>
                  {fase.fase === 'repetido' && (
                    <Table.Tr>
                      <Table.Td colSpan={3} pt={0}>
                        <AvisoDeRepetido
                          compacto
                          verificacao={fase.v}
                          pastaId={pasta.id}
                          onAbrirPasta={onAbrirPasta}
                          onResolvido={(como) => definir(i, { fase: como })}
                        />
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Fragment>
              )
            })}
          </Table.Tbody>
        </Table>
      )}
      {emOutraPasta.length >= 2 && !noLote && lote === null && (
        <Group gap="xs">
          <Button size="xs" leftSection={<IconFolderSymlink size={14} />} onClick={() => setLote('atalho')}>
            Criar atalho para os {emOutraPasta.length}
          </Button>
          {moviveis.length >= 2 && (
            <Button size="xs" variant="default" leftSection={<IconFolderShare size={14} />} onClick={() => setLote('mover')}>
              Mover os {moviveis.length} para cá
            </Button>
          )}
        </Group>
      )}
      {lote !== null && (
        <Alert color="yellow" icon={lote === 'atalho' ? <IconFolderSymlink size={14} /> : <IconFolderShare size={14} />}>
          <Stack gap="xs">
            <Text size="sm">
              {lote === 'atalho'
                ? `Criar nesta pasta um atalho para cada um dos ${doLote.length} arquivos? Os originais ficam onde estão.`
                : `Os ${doLote.length} arquivos saem das pastas onde estão e passam a seguir o acesso desta pasta.`}
            </Text>
            <Group gap="xs">
              <Button size="xs" loading={noLote} onClick={() => executarLote(lote)}>
                {lote === 'atalho' ? `Criar os ${doLote.length} atalhos` : `Mover os ${doLote.length}`}
              </Button>
              <Button size="xs" variant="default" disabled={noLote} onClick={() => setLote(null)}>Voltar</Button>
            </Group>
          </Stack>
        </Alert>
      )}
      {podeEnviar && (
        <>
          <Text size="xs" c="dimmed">Cada documento recebe o nome do arquivo como título; dá para trocar depois.</Text>
          <Select label="Ambiente" placeholder="Todos os ambientes" clearable data={ENVIRONMENT_OPTIONS} value={env} onChange={setEnv} disabled={enviando} />
          <TagsInput label="Tags" placeholder="Digite e pressione Enter" value={tags} onChange={setTags} disabled={enviando} />
          <AvisoDePrivacidade />
        </>
      )}
      <Group justify="flex-end" mt="sm">
        <Button variant="default" onClick={onClose} disabled={enviando || noLote}>{terminou ? 'Fechar' : 'Cancelar'}</Button>
        {podeEnviar && (
          <Button leftSection={<IconUpload size={14} />} loading={enviando} onClick={enviarTodos}>
            {aceitos === 1 ? 'Enviar 1 arquivo' : `Enviar ${aceitos} arquivos`}
          </Button>
        )}
      </Group>
    </Stack>
  )
}

function EnvioDeUm({ pasta, onClose, onAbrirPasta }: Props) {
  const qc = useQueryClient()
  const { maxMb } = useLimitesDaKb()
  const [file, setFile] = useState<File | null>(null)
  const [repetido, setRepetido] = useState<KbVerificacao | null>(null)
  const [conferindo, setConferindo] = useState(false)
  // Uma conferência por arquivo: a da escolha é a mesma que o envio espera.
  const conferencias = useRef(new Map<File, Promise<KbVerificacao | null>>())
  const escolhido = useRef<File | null>(null)
  const [fracao, setFracao] = useState<number | null>(null)
  const recusa = file ? recusaDe(file, maxMb) : null
  const [titulo, setTitulo] = useState('')
  const [descricao, setDescricao] = useState('')
  const [env, setEnv] = useState<string | null>(null)
  const [tags, setTags] = useState<string[]>([])

  const fechar = () => {
    setFile(null); setTitulo(''); setDescricao(''); setEnv(null); setTags([]); setFracao(null)
    setRepetido(null); setConferindo(false); escolhido.current = null; conferencias.current.clear()
    onClose()
  }

  const conferir = (f: File): Promise<KbVerificacao | null> => {
    let p = conferencias.current.get(f)
    if (!p) {
      p = verificarArquivo(pasta!.id, f)
      conferencias.current.set(f, p)
    }
    return p
  }

  // Confere na hora da escolha: quem envia sabe onde o arquivo está antes de preencher o resto.
  const escolher = (f: File | null) => {
    setFile(f)
    setRepetido(null)
    escolhido.current = f
    if (!f || !pasta || recusaDe(f, maxMb)) { setConferindo(false); return }
    setConferindo(true)
    void conferir(f).then((v) => {
      if (escolhido.current !== f) return
      setConferindo(false)
      if (ehRepetido(v)) setRepetido(v)
    })
  }

  const enviar = useMutation({
    mutationFn: async (): Promise<KbVerificacao | null> => {
      if (!pasta) throw new Error('Abra uma pasta.')
      if (!file) throw new Error('Selecione um arquivo.')
      if (recusa) throw new Error(recusa)
      if (!titulo.trim()) throw new Error('Informe o título.')
      const fd = new FormData()
      fd.append('file', file)
      fd.append('titulo', titulo.trim())
      if (descricao.trim()) fd.append('descricao', descricao.trim())
      if (env) fd.append('environment', env)
      if (tags.length) fd.append('tags', tags.join(','))
      const v = await conferir(file)
      if (ehRepetido(v)) return v
      setFracao(0)
      try {
        await uploadKbDocument(pasta.id, fd, setFracao)
      } catch (e) {
        const r = repetidoDoErro(e)
        if (r) return r
        throw e
      }
      return null
    },
    onSuccess: (v) => {
      if (v) {
        setFracao(null)
        setRepetido(v)
        return
      }
      qc.invalidateQueries({ queryKey: ['kb-documents'] })
      qc.invalidateQueries({ queryKey: ['kb-folder'] })
      qc.invalidateQueries({ queryKey: ['kb-folder-detalhes'] })
      notifications.show({ color: 'teal', title: 'Upload enviado', message: 'O documento está sendo indexado.' })
      fechar()
    },
    onError: (e: unknown) => {
      setFracao(null)
      notifications.show({ color: 'red', title: 'Erro no upload', message: mensagemDeErro(e, e instanceof Error ? e.message : 'Falha ao enviar documento.') })
    },
  })

  return (
    <Modal opened={pasta !== null} onClose={fechar} title={<Text fw={700}>Enviar documento</Text>} size="lg">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">Para: {pasta?.caminho}</Text>
        <FileInput
          label="Arquivo"
          placeholder="PDF, DOCX, XLSX, PPTX, TXT, CSV ou imagem"
          accept={MIME_ACCEPT}
          value={file}
          onChange={escolher}
          required
          leftSection={<IconUpload size={14} />}
          description={`Tamanho máximo: ${maxMb} MB`}
          error={recusa}
          disabled={enviar.isPending}
        />
        {conferindo && <Text size="xs" c="dimmed">Conferindo se o arquivo já está na base…</Text>}
        {repetido ? (
          <AvisoDeRepetido
            verificacao={repetido}
            pastaId={pasta!.id}
            onAbrirPasta={onAbrirPasta ? (id) => { fechar(); onAbrirPasta(id) } : undefined}
            onCancelar={fechar}
            onResolvido={fechar}
          />
        ) : (
          <>
            <TextInput label="Título" value={titulo} onChange={(e) => setTitulo(e.currentTarget.value)} required />
            <Textarea label="Descrição" value={descricao} onChange={(e) => setDescricao(e.currentTarget.value)} rows={3} />
            <Select label="Ambiente" placeholder="Todos os ambientes" clearable data={ENVIRONMENT_OPTIONS} value={env} onChange={setEnv} />
            <TagsInput label="Tags" placeholder="Digite e pressione Enter" value={tags} onChange={setTags} />
            <AvisoDePrivacidade />
          </>
        )}
        {enviar.isPending && fracao !== null && (
          <Stack gap={4}>
            <Progress value={fracao * 100} aria-label="Progresso do envio" />
            <Text size="xs" c="dimmed" ff="monospace">
              {fracao >= 1 ? 'Arquivo enviado; guardando…' : `Enviando ${porcento(fracao)}`}
            </Text>
          </Stack>
        )}
        {repetido?.situacao === 'outra_pasta' ? null : repetido ? (
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={fechar}>Fechar</Button>
          </Group>
        ) : (
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={fechar} disabled={enviar.isPending}>Cancelar</Button>
            <Button leftSection={<IconUpload size={14} />} loading={enviar.isPending} onClick={() => enviar.mutate()}>
              Enviar documento
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  )
}
